import {Transition} from '@aboutyou/transitions';
import {isElementFullyInViewport} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import debounce from 'lodash.debounce';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {FadePanel} from '../../atoms/FadePanel/FadePanel';
import {Text as UnStyledText} from '../../atoms/Text/Text';
import {DefaultToggleIcon} from './DefaultToggleIcon';

export interface ToggleIconProps {
  isOpen: boolean;
}

interface AccordionProps {
  id?: string;
  withContentTeaser?: boolean;
  toggleContent?: string | React.ReactElement;
  content: string | React.ReactElement;
  className?: string;
  style?: React.CSSProperties;
  scrollTitleInView?: boolean;
  onOpen?: VoidFunction;
  onClose?: VoidFunction;
  trackClose?: VoidFunction;
  trackOpen?: VoidFunction;
  'data-testid'?: string;
  ToggleIcon?: React.ComponentType<ToggleIconProps>;
  alwaysRenderContent?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
  id,
  withContentTeaser,
  toggleContent,
  content,
  className,
  style,
  scrollTitleInView,
  onOpen,
  onClose,
  trackClose,
  trackOpen,
  'data-testid': dataTestid = 'accordionContainer',
  ToggleIcon = DefaultToggleIcon,
  alwaysRenderContent,
}) => {
  const [toggleActive, setToggle] = useState(false);
  const [height, setHeight] = useState(0);
  const [toggleContentHeight, setToggleContentHeight] = useState(0);
  const toggleContentRef = useRef<HTMLDivElement | null>(null);
  const openerButtonRef = useRef<HTMLButtonElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const accordionContentContainerId = useMemo(
    () => `accordionContentContainer${id ? '-' + id : ''}`,
    [id],
  );

  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      toggleContentRef.current = node;
      setToggleContentHeight(node.clientHeight);
    }
  }, []);

  const onClickHandler = () => {
    toggleActive ? trackClose?.() : trackOpen?.();
    setToggle(!toggleActive);
  };

  // The onOpenHandler function is called when an accordion is opened.
  // If the scrollTitleInView prop is true, it adds a 'transitionend' event listener
  // to the ContentContainer. When the transition ends, it checks if the accordion
  // title is fully in the viewport. If not, it scrolls the title into view smoothly.
  // Finally, it removes the event listener to prevent memory leaks.
  const onOpenHandler = useCallback(() => {
    onOpen?.();

    if (!scrollTitleInView) {
      return;
    }

    const debouncedScrollIntoView = debounce(() => {
      openerButtonRef.current?.scrollIntoView({behavior: 'smooth'});
    }, 200);

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'max-height') {
        if (!isElementFullyInViewport(openerButtonRef.current as HTMLElement)) {
          debouncedScrollIntoView();
        }
        contentContainerRef.current?.removeEventListener(
          'transitionend',
          handleTransitionEnd,
        );
      }
    };
    contentContainerRef.current?.addEventListener(
      'transitionend',
      handleTransitionEnd,
    );
  }, [onOpen, scrollTitleInView]);

  useEffect(() => {
    if (toggleActive) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setHeight(toggleContentHeight);
      onOpenHandler();
    } else if (!toggleActive && withContentTeaser) {
      setHeight(60);
      onClose?.();
    } else {
      setHeight(0);
      onClose?.();
    }
  }, [
    toggleContentHeight,
    toggleActive,
    withContentTeaser,
    onClose,
    onOpenHandler,
  ]);

  const contentWithRef = (
    <div ref={measuredRef}>
      {typeof toggleContent === 'string' ? (
        <Text>{toggleContent}</Text>
      ) : (
        toggleContent
      )}
    </div>
  );

  return (
    <Wrapper data-testid={dataTestid} className={className} style={style}>
      <TitleContainer
        aria-controls={accordionContentContainerId}
        aria-pressed="false"
        onClick={onClickHandler}
        data-testid="accordionTitleContainer"
        ref={openerButtonRef}
        aria-label={undefined}
        aria-expanded={toggleActive}
      >
        {typeof content === 'string' ? <Text>{content}</Text> : content}
        <ToggleIcon isOpen={Boolean(toggleActive && toggleContent)} />
      </TitleContainer>
      <StyledFadePanel
        position={'bottom'}
        size={'5rem'}
        visible={Boolean(withContentTeaser) && !toggleActive}
      >
        <ContentContainer
          id={accordionContentContainerId}
          $height={height}
          data-testid="accordionContent"
          aria-hidden={!toggleActive}
          ref={contentContainerRef}
          inert={!toggleActive}
        >
          {alwaysRenderContent ? (
            <>{contentWithRef}</>
          ) : (
            <Transition
              duration={0}
              delay={{
                mount: 0,
                unmount: 120,
              }}
              nodeRef={toggleContentRef}
              mounted={toggleActive || Boolean(withContentTeaser)}
            >
              {contentWithRef}
            </Transition>
          )}
        </ContentContainer>
      </StyledFadePanel>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background-color: ${COLORS.White};
  padding: 16px;
  border: solid 1px ${COLORS.Grey3};
  border-bottom: none;
  -webkit-tap-highlight-color: transparent;

  &:last-of-type {
    border-bottom: solid 1px ${COLORS.Grey3};
  }

  &:hover {
    cursor: pointer;
  }

  ${tabletAndUpLinaria(`
    &:first-of-type {
      border-radius: 5px 5px 0px 0px;
    }

    &:last-of-type {
      border-radius: 0px 0px 5px 5px;
      border-bottom: solid 1px ${COLORS.Grey3};
    }
  `)}
`;

export const TitleContainer = styled(UnstyledButton)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  text-align: left;

  & > :last-child {
    margin-left: auto;
  }
`;

export const ContentContainer = styled.div<{$height: number}>`
  max-height: ${({$height}) => `${$height}px`};
  margin-top: ${({$height}) => ($height > 0 ? '1.2rem' : 0)};
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: max-height 120ms ease-in-out 0ms, margin-top 120ms ease-in-out 0ms;
  overflow: hidden;
`;

const Text = styled(UnStyledText)`
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: ${COLORS.Grey1};
`;

const StyledFadePanel = styled(FadePanel)`
  cursor: text;
`;
